<template>
  <div class="mt-5">
    <form autocomplete="off" @submit.prevent="generateAuthCode">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-12 col-w-full col-lg-8">
            <div class="card">
              <div class="d-flex justify-content-center">
                <h3 class="font-weight-bold">Alterar Senha</h3>
              </div>
              <hr />
              <div class="card-content">
                <div class="row justify-content-center">
                  <div class="container-fluid col-8">
                    <div class="form-group col-12 col-w-full col-md-10 col-lg-12 col-xl-10 mb-4">
                      <label class="font-weight-bold">Digite a sua senha atual:</label>
                      <PasswordStrength
                        v-model="oldPassword"
                        :show-strength-meter="false"
                        :secure-length="6"
                      />
                    </div>
                    <div class="form-group col-12 col-w-full col-md-10 col-lg-12 col-xl-10 mb-4">
                      <label class="font-weight-bold">Digite a sua nova senha:</label>
                      <PasswordStrength
                        v-model="password"
                        :show-strength-meter="true"
                        :secure-length="8"
                        :toggle="true"
                        :maxlength="20"
                      />
                      <small
                        v-if="password.length > 0 && v$.password && v$.password.$invalid"
                        class="text-danger"
                      >
                        A Senha está no formato inválido, Exemplo: Ex4plo@123.
                      </small>
                    </div>
                    <div class="form-group col-12 col-w-full col-md-10 col-lg-12 col-xl-10 mb-4">
                      <label class="font-weight-bold">Repita a sua nova senha:</label>
                      <PasswordStrength
                        v-model="confirmPassword"
                        :show-strength-meter="true"
                        :secure-length="8"
                        :toggle="true"
                        :maxlength="20"
                      />
                      <small
                        v-if="
                          confirmPassword.length > 0 &&
                          v$.confirmPassword &&
                          !v$.confirmPassword.sameAsPassword
                        "
                        class="text-danger"
                        >Confirme sua senha</small
                      >
                    </div>
                  </div>
                  <div class="container-fluid col-4">
                    <h5 class="font-weight-bold">Como sua senha deve ser</h5>
                    <p class="mb-3">
                      <strong>Não coloque</strong> o seu nome, sobrenome, e-mail, nem caracteres
                      idênticos e consecutivos.
                    </p>
                    <p class="mb-0">
                      A senha deve ter entre <strong>8 e 20 caracteres</strong> e conter pelo menos
                      <strong
                        >1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere
                        especial</strong
                      >.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row justify-content-center mt-4">
          <div class="col-10 col-lg-4">
            <button
              class="btn btn-primary p-3 h4 w-100 font-weight-bold"
              :disabled="v$ && v$.$invalid"
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { required, sameAs, minLength } from '@vuelidate/validators';
import { password as passwordValidator } from '@/validations/password';
import Swal from 'sweetalert2';
import { useStore } from 'vuex';
import AccountApi from '@/services/AccountApi';
import { Presets } from '@/models/Presets';
import Alert from '@/models/Alert';
import PasswordStrength from '../PasswordStrength.vue';
import CreateNewPasswordError from '@/errors/CreateNewPasswordError';

export default {
  name: 'AppPasswordChange',
  components: { PasswordStrength },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const instance = getCurrentInstance();
    const $bus = instance.appContext.config.globalProperties.$bus;

    const oldPassword = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const authCode = ref('');
    const preset = ref(new Presets());
    const authCodeRegex = new RegExp('^[0-9]+$');
    const userEmail = ref(route.params?.userEmail ?? '');

    const rules = computed(() => ({
      oldPassword: { required, minLength: minLength(6) },
      password: { required, minLength: minLength(8), password: passwordValidator },
      confirmPassword: { required, sameAsPassword: sameAs(password) },
    }));

    const v$ = useVuelidate(rules, {
      oldPassword,
      password,
      confirmPassword,
    });

    const keepToken = token => store.dispatch('account/keepToken', token);

    const loadPresets = async () => {
      const api = new AccountApi();
      $bus.emit('spinner-run');
      preset.value = await api.employeePresets();
      $bus.emit('spinner-stop');
    };

    onMounted(async () => {
      if (!userEmail.value) {
        await loadPresets();
      }
    });

    const handleResponse = async response => {
      $bus.emit('spinner-stop');

      if (response.status === 422) {
        const alert = new Alert();
        alert.title = 'Ops';
        alert.error = new CreateNewPasswordError(response.data).getMessage();
        alert.confirmButtonText = 'Ok';
        $bus.emit('alert-error', alert);
      } else if (response.status === 200) {
        keepToken(response.data.token);
        window.$alert.info('Pronto!', 'Senha cadastrada com sucesso.');
        router.push({ name: 'root' });
      } else {
        const alert = new Alert();
        alert.title = 'Ops';
        alert.error =
          response.status === 400 ? 'Não foi possível atualizar a senha.' : 'Erro inesperado!';
        alert.confirmButtonText = 'Ok';
        $bus.emit('alert-error', alert);
      }
    };

    const sendNewPassword = async () => {
      const api = new AccountApi();
      $bus.emit('spinner-run');
      const response = await api.changePassword(oldPassword.value, password.value, authCode.value);
      await handleResponse(response);
    };

    const sendNewExpiredPassword = async () => {
      const api = new AccountApi();
      $bus.emit('spinner-run');
      const response = await api.changeExpiredPassword(
        userEmail.value,
        oldPassword.value,
        password.value,
        authCode.value
      );
      await handleResponse(response);
    };

    const generateAuthCode = async () => {
      const api = new AccountApi();
      const mobile = preset.value.holder.mobile;
      const email = preset.value.holder.email;

      const { value } = await Swal.fire({
        title: 'Enviaremos um código para autenticação',
        input: 'select',
        inputOptions: {
          email: `Email : ${email}`,
          mobile: `Celular : ${mobile}`,
        },
        inputPlaceholder: 'Selecione por onde você deseja receber',
        showCancelButton: true,
        preConfirm: value => {
          if (!value) {
            Swal.showValidationMessage('Você deve selecionar uma opção!');
          }
        },
        allowOutsideClick: () => !Swal.isLoading(),
      });

      if (!value) {
        return;
      }

      $bus.emit('spinner-run');
      const authCodeModel = {
        mobile: value === 'mobile',
        ...(userEmail.value && {
          login: userEmail.value,
          password: oldPassword.value,
        }),
      };

      const response = userEmail.value
        ? await api.sendAuthCodeExpiredPassword(authCodeModel)
        : await api.sendAuthCodeByType(authCodeModel, 'change-password');

      $bus.emit('spinner-stop');

      if (response.status !== 204) {
        return window.$alert.error('Ops, erro durante envio de código');
      }

      const inputResponse = await Swal.fire({
        title: 'Concluir alteração de dados',
        html: '<input type="text" maxlength="6" id="swal-input-authCode" class="form-control text-center" placeholder="Informe o código recebido">',
        confirmButtonText: 'Confirmar',
        showLoaderOnConfirm: true,
        showCloseButton: true,
        preConfirm: () => {
          const code = document.getElementById('swal-input-authCode').value;
          if (!code) {
            Swal.showValidationMessage('Campo obrigatório!');
          } else if (!authCodeRegex.test(code)) {
            Swal.showValidationMessage('Digite apenas números!');
          }
          return code;
        },
        allowOutsideClick: () => !Swal.isLoading(),
      });

      if (inputResponse.isConfirmed) {
        authCode.value = inputResponse.value;
        if (!userEmail.value) {
          await sendNewPassword();
        } else {
          await sendNewExpiredPassword();
        }
      }
    };

    return {
      oldPassword,
      password,
      confirmPassword,
      authCode,
      preset,
      userEmail,
      v$,
      generateAuthCode,
    };
  },
};
</script>

<style scoped>
.card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: none;
}

.card-content {
  padding: 2rem 1.5rem;
}

label {
  margin-bottom: 0.75rem;
  color: #495057;
  font-size: 0.95rem;
}

.form-group {
  margin-bottom: 2rem;
}

h5 {
  color: #495057;
  margin-bottom: 1rem;
}

.btn-primary {
  border-radius: 8px;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  transition: all 0.2s ease-in-out;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

hr {
  margin: 1rem 0;
  border-color: #e9ecef;
}

.container-fluid.col-4 p {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #6c757d;
}

.progress {
  height: 6px;
  border-radius: 3px;
  background-color: #e9ecef;
}

.progress-bar {
  transition: width 0.3s ease-in-out;
  border-radius: 3px;
}

.input-group-append .btn {
  border-left: none;
  background-color: #f8f9fa;
  border-color: #ced4da;
}

.input-group-append .btn:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.input-group-append .btn:focus {
  box-shadow: none;
  border-color: #80bdff;
}

h3 {
  margin: 1.5rem 0 0.5rem 0;
  color: #495057;
}
</style>
